import React from 'react';
import styled from 'astroturf/react';
import WorkCard from './WorkCard';
import { Button } from 'js/design_system/Button';
import Badge from 'js/design_system/Badge';
import { WorkData, EvaluationVote } from '../types';

const CardContainer = styled.div`
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  position: relative;
`;

const PositionBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`;

const WorkCardWrapper = styled.div`
  padding-right: 48px;
`;

const VoteButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--stroke-light);
`;

interface WorkEvaluationCardProps {
  workData: WorkData;
  vote: EvaluationVote | null;
  onVote: (vote: EvaluationVote) => void;
  index: number;
}

export const WorkEvaluationCard: React.FC<WorkEvaluationCardProps> = ({
  workData,
  vote,
  onVote,
  index,
}) => {
  const getButtonStyle = (voteType: EvaluationVote) => {
    const isSelected = vote === voteType;

    switch (voteType) {
      case 'would_cite':
        return isSelected
          ? {
              backgroundColor: 'var(--brand-colors-success-500)',
              color: 'var(--text-white)',
              borderColor: 'var(--brand-colors-success-900)',
            }
          : {
              backgroundColor: 'var(--brand-colors-success-100)',
              color: 'var(--brand-colors-success-900)',
              borderColor: 'var(--brand-colors-success-500)',
            };
      case 'would_not_cite':
        return isSelected
          ? {
              backgroundColor: 'var(--brand-colors-error-500)',
              color: 'var(--text-white)',
              borderColor: 'var(--brand-colors-error-900)',
            }
          : {
              backgroundColor: 'var(--brand-colors-error-100)',
              color: 'var(--brand-colors-error-500)',
              borderColor: 'var(--brand-colors-error-500)',
            };
      case 'maybe_depends':
        return isSelected
          ? {
              backgroundColor: 'var(--brand-colors-neutral-600)',
              color: 'var(--text-white)',
              borderColor: 'var(--brand-colors-neutral-700)',
            }
          : {
              backgroundColor: 'var(--brand-colors-neutral-200)',
              color: 'var(--brand-colors-neutral-900)',
              borderColor: 'var(--brand-colors-neutral-500)',
            };
      default:
        return {};
    }
  };

  return (
    <CardContainer
      style={{
        background:
          vote !== null ? 'var(--brand-colors-neutral-300)' : undefined,
      }}
    >
      <PositionBadge>
        <Badge
          text={`${index + 1}`}
          backgroundColor="var(--brand-colors-blue-600)"
          color="var(--text-white)"
          height="28px"
          style={{
            fontWeight: 700,
            fontSize: '16px',
            minWidth: '32px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
          }}
        />
      </PositionBadge>
      <WorkCardWrapper>
        <WorkCard
          workData={workData}
          showAddToListButton={false}
          showPaperEvaluationChatButton={false}
          hasVote={vote !== null}
        />
      </WorkCardWrapper>
      <VoteButtonContainer>
        <Button
          size="sm"
          onClick={() => onVote('would_cite')}
          icon="check_circle"
          style={{
            flex: 1,
            fontSize: '13px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            ...getButtonStyle('would_cite'),
          }}
        >
          Would Cite
        </Button>
        <Button
          size="sm"
          onClick={() => onVote('would_not_cite')}
          icon="cancel"
          style={{
            flex: 1,
            fontSize: '13px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            ...getButtonStyle('would_not_cite'),
          }}
        >
          Won't Cite
        </Button>
        <Button
          size="sm"
          onClick={() => onVote('maybe_depends')}
          icon="help"
          style={{
            flex: 1,
            fontSize: '13px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            ...getButtonStyle('maybe_depends'),
          }}
        >
          Maybe
        </Button>
      </VoteButtonContainer>
    </CardContainer>
  );
};
